<script lang="ts">
	import { afterNavigate } from '$app/navigation';
	import AmountInput from '$lib/AmountInput.svelte';
	import Button from '$lib/Button.svelte';
	import Collapsible from '$lib/Collapsible.svelte';
	import Select from '$lib/Select.svelte';
	import { schema, type SsbSwap } from '$lib/schemas/ssb-swap-calculator';
	import { getSignedColor } from '$lib/utils';
	import type { SelectOption } from '@melt-ui/svelte';
	import dayjs from 'dayjs';
	import { tick } from 'svelte';
	import { defaults, superForm } from 'sveltekit-superforms';
	import { zod } from 'sveltekit-superforms/adapters';
	import { calculateSsbSwap, toSsbSwap } from '../../tools/ssb-swap-calculator/utils';
	import type { PageData } from './$types';

	const { form, errors, enhance, allErrors } = superForm(
		defaults(
			{
				currentSsb: undefined
			},
			zod(schema)
		),
		{
			SPA: true,
			validators: zod(schema),
			dataType: 'json',
			resetForm: false,
			onUpdate({ form }) {
				if (form.valid) {
					swapResults = calculateSsbSwap(form.data.currentSsb, form.data.newSsb, form.data.amount);
					void tick().then(() => {
						resultsSection?.scrollIntoView({
							behavior: 'smooth'
						});
					});
				}
			}
		}
	);

	interface Props {
		data: PageData;
	}

	let { data }: Props = $props();
	let { singaporeSavingsBonds, projectedSSB } = $derived(data);

	let announcedSSB: BaseSingaporeSavingsBond | undefined = $state();
	let defaultSelectedCurrentSsb: SelectOption<SsbSwap> | undefined;
	let defaultSelectedNewSsb: SelectOption<SsbSwap> | undefined = $state();
	let currentSsbOptions: SelectOption<SsbSwap>[] = $state([]);
	let newSsbOptions: SelectOption<SsbSwap>[] = $state([]);

	afterNavigate(() => {
		if (singaporeSavingsBonds) {
			announcedSSB = singaporeSavingsBonds[0];
			currentSsbOptions = singaporeSavingsBonds
				.slice(1)
				.map(toSsbSwap)
				.map((swap) => ({
					label: `${swap.name} (${swap.issueCode})`,
					value: swap
				}));

			if (announcedSSB && projectedSSB) {
				let accuracy =
					dayjs(projectedSSB.lastUpdated).date() < 11
						? 'Low'
						: dayjs().date() < 21
							? 'Medium'
							: 'High';
				newSsbOptions = [announcedSSB]
					.map(toSsbSwap)
					.map((swap) => ({
						label: swap.name + ' [Announced]',
						value: swap
					}))
					.concat(
						accuracy === 'High'
							? [
									{
										label: `SB${dayjs(announcedSSB.issueDate)
											.add(1, 'month')
											.format('MMMYY')
											.toUpperCase()} [Projected]`,
										value: {
											name: `SB${dayjs(announcedSSB.issueDate)
												.add(1, 'month')
												.format('MMMYY')
												.toUpperCase()}`,
											issueCode: '',
											issueDate: projectedSSB.lastUpdated,
											interestRates: projectedSSB.interestRates
										}
									}
								]
							: []
					);
				defaultSelectedNewSsb = newSsbOptions[0];
			}
		}
	});

	let swapResults: SwapResult[] = $state([]);
	let totalProfitLoss = $derived(swapResults[swapResults.length - 1]?.cumulativeProfitLoss);

	let resultsSection: HTMLElement | undefined = $state();
</script>

{#if singaporeSavingsBonds && projectedSSB && announcedSSB}
	<section>
		<h3 class="h3 mb-6">Choose SSB to swap and enter investment amount</h3>

		<form use:enhance class="mx-auto max-w-lg">
			<Select
				class="mb-8"
				label="Redeem existing"
				placeholder="Select an existing SSB"
				defaultSelected={defaultSelectedCurrentSsb}
				bind:selectedValue={$form.currentSsb}
				options={currentSsbOptions}
				ids={{ menu: 'currentSsb_menu' }}
				errors={$errors.currentSsb?._errors}
			/>
			<Select
				class="mb-3"
				label="Swap to"
				defaultSelected={defaultSelectedNewSsb}
				bind:selectedValue={$form.newSsb}
				options={newSsbOptions}
				ids={{ menu: 'newSsb_menu' }}
			/>
			<p class="index-regular text-gray-mid mb-8">
				<span class="index-bold">Tip:</span>
				Check back at month-end to swap with
				<a
					href="/tools/ssb-interest-rate-projection"
					class="link-primary index-bold"
					target="_parent">the next projected SSB</a
				>
			</p>
			<AmountInput
				label="Investment amount"
				id="amount"
				bind:value={$form.amount}
				placeholder="Min $500"
				errors={$errors.amount}
				class="mb-3"
				type="currency"
			/>

			<p class="index-regular text-gray-mid mb-8">
				<span class="index-bold">Tip:</span> You may not be allotted the full amount that you have redeemed
				from previous subscriptions
			</p>

			<Button disabled={$allErrors.length > 0} class="w-full">Calculate</Button>
		</form>
	</section>

	{#if swapResults.length}
		<div bind:this={resultsSection} class="scroll-target mx-auto mt-6 max-w-lg">
			<section class="bg-ivory mb-5 flex flex-col justify-center rounded-xl p-6 text-center">
				<p class="text-gray-dark mb-3 lg:mb-6">
					Swapping <span class="body-bold text-black">{$form.currentSsb?.name}</span> with
					<span class="body-bold text-black">{$form.newSsb?.name}</span> will earn you
				</p>
				<p
					style="color: var(--color-{getSignedColor(totalProfitLoss)})"
					class="max-serif mb-3 lg:mb-6"
				>
					{totalProfitLoss.toLocaleString('default', {
						style: 'currency',
						currency: 'SGD',
						currencyDisplay: 'narrowSymbol',
						signDisplay: 'exceptZero'
					})}
				</p>
				{#if totalProfitLoss > 0}
					<p class="card-bold text-gray-dark">
						That can buy you
						<span class="text-black">{Math.floor(totalProfitLoss / 3)} cups of coffee! ☕</span>
						<br />
						<a href="/tools/ssb-interest-rate-projection" class="link-primary" target="_parent">
							See our projection for the next SSB ️️
						</a>
					</p>
				{:else}
					<p class="card-bold text-gray-dark">
						This is a negative swap.<br />
						<a href="/tools/ssb-interest-rate-projection" class="link-primary" target="_parent">
							See our projection for the next SSB</a
						>️ to see if you can get a higher rate
					</p>
				{/if}
			</section>

			<div class="mb-5">
				<Button
					to={{
						href: `/tools/ssb-swap-calculator?currentSsb=${$form.currentSsb?.name}&newSsb=${$form.newSsb?.name}&amount=${$form.amount}`,
						target: '_parent'
					}}
					variant="secondary">See monthly breakdown</Button
				>
			</div>
			<Collapsible title="See full disclaimer">
				<p class="small-regular text-gray-dark">
					*Disclaimer: All investments carry risk, including the risk that the investor may lose all
					or part of his investment. The data and output from this tool does not constitute
					investment advice and is not a personal recommendation from Beansprout. It does not have
					regard to the specific investment objectives, financial situation and particular needs of
					any persons. Nothing contained herein should be construed as an offer or solicitation for
					the subscription, purchase or sale of any particular investment product or class of
					investment product. Any investment activities undertaken using this tool will be at the
					sole risk of the relevant investor. Any visitor to this page agrees to hold Beansprout
					harmless against any claims for damages arising from any decisions that the visitor makes
					based on such information
				</p>
			</Collapsible>
		</div>
	{/if}
{:else}
	<p class="text-center">
		The latest Singapore Savings Bond SB{dayjs().add(1, 'month').format('MMM').toUpperCase()}24 has
		not been announced. <br /> Please check back again later.
	</p>
{/if}
